/**
 * Public church donation page — uses Stripe Connect destination charge.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Heart, ArrowRight } from "lucide-react";

const PRESETS = [500, 1000, 2500, 5000, 10000]; // pence
const inp = "w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-3 py-2.5 rounded text-sm text-white";

export default function PublicChurchDonate() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const [amount, setAmount] = useState(2500);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [giftAid, setGiftAid] = useState(false);
  const [church, setChurch] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/churches/${slug}`);
        setChurch(data.church);
      } catch { /* not found / unpublished */ }
    })();
  }, [slug]);

  const cancelled = search.get("cancelled");

  const finalAmount = useMemo(() => {
    if (custom) {
      const n = parseFloat(custom);
      if (!Number.isNaN(n) && n > 0) return Math.round(n * 100);
    }
    return amount;
  }, [amount, custom]);

  const submit = async () => {
    if (finalAmount < 100) return toast.error("Minimum donation is £1");
    setRedirecting(true);
    try {
      const { data } = await api.post(`/churches/${slug}/donate`, {
        amount_pence: finalAmount,
        currency: "gbp",
        donor_name: name || null,
        donor_email: email || null,
        message: message || null,
        is_gift_aid: giftAid,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Donation failed");
      setRedirecting(false);
    }
  };

  return (
    <div data-testid="public-donate" className="max-w-2xl mx-auto px-6 py-16 text-white">
      <div className="text-[10px] tracking-widest uppercase text-[#D4AF37]">Give</div>
      <h1 className="font-serif-display text-4xl mt-2">Support <span className="italic text-[#D4AF37]">{church?.name || slug}</span></h1>
      <p className="text-neutral-300 mt-3 text-sm leading-relaxed">Your gift goes directly to the church via Stripe. We never see your card details.</p>

      {cancelled && <div className="mt-5 rounded border border-amber-500/40 bg-amber-900/20 text-amber-200 px-4 py-2.5 text-xs">Donation cancelled. You haven&apos;t been charged.</div>}

      <div className="mt-8 luxury-card rounded-xl p-6 space-y-5">
        <div>
          <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-2">Amount</div>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {PRESETS.map((p) => (
              <button key={p} data-testid={`donate-preset-${p}`} type="button" onClick={() => { setAmount(p); setCustom(""); }}
                className={`px-3 py-2.5 rounded border text-sm ${!custom && amount === p ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/10 text-neutral-300"}`}>
                £{(p / 100).toFixed(0)}
              </button>
            ))}
          </div>
          <div className="mt-3">
            <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1.5">Or enter a custom amount (£)</div>
            <input data-testid="donate-custom" className={inp} placeholder="25" value={custom} onChange={(e) => setCustom(e.target.value)} type="number" min="1" step="0.01" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="donate-name" className={inp} placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} />
          <input data-testid="donate-email" className={inp} placeholder="Email (for receipt)" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <textarea className={inp} rows={3} placeholder="Message (optional)" value={message} onChange={(e) => setMessage(e.target.value)} />
        <label className="inline-flex items-start gap-2 text-xs text-neutral-300">
          <input type="checkbox" checked={giftAid} onChange={(e) => setGiftAid(e.target.checked)} className="mt-0.5" />
          <span>I am a UK taxpayer and want this church to claim Gift Aid on my donation.</span>
        </label>

        <div className="flex items-center justify-between pt-2 flex-wrap gap-3">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-neutral-500">Total</div>
            <div className="font-serif-display text-3xl text-[#D4AF37]">£{(finalAmount / 100).toFixed(2)}</div>
          </div>
          <button data-testid="donate-submit" disabled={redirecting} onClick={submit} className="btn-gold px-6 py-3 rounded text-xs tracking-widest uppercase font-semibold inline-flex items-center gap-2">
            {redirecting ? <Loader2 size={14} className="animate-spin" /> : <Heart size={14} />} Give £{(finalAmount / 100).toFixed(2)} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
